import { InfoIcon } from "@phosphor-icons/react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { SidebarMenuButton } from "@/components/ui/sidebar";
import { getNavItems, getNavItemsForRole } from "@/config/navigation";
import type { Lang } from "@/config/routes";
import { useAuth } from "@/contexts/auth-context";
import type { UserRole } from "@/types/auth";

const ROLE_LABEL_KEYS: Record<UserRole, string> = {
	admin: "roles.admin",
	district_manager: "roles.districtManager",
	reseller_manager: "roles.resellerManager",
	reseller: "roles.reseller",
	end_user: "roles.endUser",
};

export function AboutSystemAlertDialog({
	role,
	lang,
}: {
	role: UserRole;
	lang: Lang;
}) {
	const { t } = useTranslation();
	const { user } = useAuth();
	const tabs =
		role === "admin"
			? getNavItems(t, lang)
			: getNavItemsForRole(t, lang, role, user);
	const features = t(`about.roles.${role}.features`, {
		returnObjects: true,
	}) as string[];

	return (
		<Dialog>
			<DialogTrigger asChild>
				<SidebarMenuButton tooltip={t("about.trigger")}>
					<InfoIcon aria-hidden="true" />
					<span>{t("about.trigger")}</span>
				</SidebarMenuButton>
			</DialogTrigger>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{t("about.title")}</DialogTitle>
					<DialogDescription>{t("about.intro")}</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4">
					<section className="flex flex-col gap-1">
						<p className="text-xs font-medium uppercase text-muted-foreground">
							{t("about.yourRole")}
						</p>
						<p className="text-sm font-semibold">{t(ROLE_LABEL_KEYS[role])}</p>
						<p className="text-sm text-muted-foreground">
							{t(`about.roles.${role}.description`)}
						</p>
					</section>
					<section className="flex flex-col gap-1.5">
						<p className="text-xs font-medium uppercase text-muted-foreground">
							{t("about.features")}
						</p>
						<ul className="list-disc space-y-1 pl-5 text-sm">
							{features.map((feature) => (
								<li key={feature}>{feature}</li>
							))}
						</ul>
					</section>
					<section className="flex flex-col gap-1.5">
						<p className="text-xs font-medium uppercase text-muted-foreground">
							{t("about.availableTabs")}
						</p>
						<ul className="flex flex-wrap gap-1.5">
							{tabs.map((tab) => (
								<li key={tab.title}>
									<Badge variant="secondary">
										<tab.icon aria-hidden="true" />
										{tab.title}
									</Badge>
								</li>
							))}
						</ul>
					</section>
				</div>
				<DialogFooter>
					<DialogPrimitive.Close asChild>
						<Button variant="outline">{t("about.close")}</Button>
					</DialogPrimitive.Close>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
